import { handleDeleteRequest, handleGetRequest, handlePostRequest, handlePutRequest } from '.';
import { moduleName } from '../../constants';
import { URI } from '../../constants/URI';
import { setCustomer } from '../slices/filtersSlice';
import { appendOrderItems, reinsertOrderItem, removeOrderItemById, setOrderItems, upsertOrderItem } from '../slices/orderSlice';
import { appendOpenOrderItems, appendOpenOrderItemToProducts, reinsertOpenOrderItem, removeOpenOrderItemById } from '../slices/productsSlice';
import { forgetSuggestedProducts, seedCustomerData, seedCustomerOption } from '../../queries/customerData';
import { customerKeys } from '../../queries/keys';
import { editOrderItem as editSavedItem, waitForItemEdits, withPendingEdits } from '../pendingOrderEdits';
import { store } from '../store';
import { buildOrderItem, discardPendingItem, isPendingId, PENDING_ITEMS_MESSAGE, pendingItemsFor, startPendingItem } from '../pendingOrderItem';

export const addOrder = async (body, { toast = false } = {}) => {
    const res = await handlePostRequest(URI.order, body, {
        module: moduleName.ORDER,
        toast,
    });
    return res;
};

export const updateOrder = async (body, { isGet = true } = {}) => {
    const res = await handlePutRequest(URI.order, body, {
        module: moduleName.ORDER,
        toast: false,
    });
    if (res?.ack && isGet) {
        getOrdersById(body.id);
    }
    return res;
};

export const addOrderItem = async (body, product, isAppend = true, { toast = false, signal, pendingId, ...options } = {}) => {
    if (isPendingId(body?.id)) {
        return { ack: false, message: PENDING_ITEMS_MESSAGE };
    }

    const isNewItem = isAppend && !!product && !body?.id;
    const placeholderId = pendingId ?? (isNewItem ? startPendingItem(body, product) : null);

    // An added item comes back as the order shows it (tiers, units, stock), so the order is not reloaded.
    const res = await handlePostRequest(URI.order_item, isAppend ? { ...body, return_item: 1 } : body, {
        module: moduleName.ORDER_ITEM,
        loading: false,
        toast,
        signal,
        ...options,
    });

    if (placeholderId) discardPendingItem(placeholderId);
    if (res?.ack) forgetSuggestedProducts();

    if (res?.ack && isAppend) {
        const order = buildOrderItem(product, res.record_id, body?.quantity);
        // An older server answers without the item; the row built from the product is then used as before.
        if (res.order_item?.length) store.dispatch(upsertOrderItem(res.order_item));
        else store.dispatch(appendOrderItems(order));
        store.dispatch(appendOpenOrderItems(order));
    }

    return res;
};

export const getOrders = async (body) => {
    const res = await handlePostRequest(URI.orders, body, {
        module: moduleName.ORDER,
        toast: false,
    });
    return res;
};

export const deleteOrder = async (orderId, { toast = true } = {}) => {
    const res = await handleDeleteRequest(
        URI.order,
        { id: orderId },
        {
            module: moduleName.ORDER,
            toast,
        }
    );
    return res;
};

// With `withCustomer` the answer also carries the order's customer profile; the page's customer panel
// then finds it already loaded instead of making its own request.
export const getOrdersById = async (orderId, { toast = false, withCustomer = false } = {}) => {
    const res = await handleGetRequest(URI.order, null, {
        module: moduleName.ORDER,
        params: {
            id: orderId,
            ...(withCustomer ? { with_customer: 1 } : {}),
        },
        toast,
    });
    const customerId = res?.order?.customer_netsuite_id;
    if (res?.ack && customerId && res.customer?.customer_details?.length) {
        seedCustomerData(customerKeys.profile(customerId), { ack: true, ...res.customer });
        seedCustomerOption(res.customer.customer_details[0]);
    }
    return applyOrderResponse(res, orderId);
};

// Puts a loaded order into the store; also used for an order that arrived with another response.
export const applyOrderResponse = (res, orderId) => {
    // Edits still being saved stay on screen; the server has not seen them yet.
    const savedItems = withPendingEdits(res?.order_details || []);
    store.dispatch(setOrderItems([...savedItems, ...pendingItemsFor(orderId, savedItems)]));
    store.dispatch(appendOpenOrderItems(savedItems));
    store.dispatch(setCustomer(res?.order?.customer_netsuite_id || null));
    return res;
};

export const finishRemoveOrderItem = (orderItemId, orderId, product = null, { reload = true } = {}) => {
    if (product) {
        store.dispatch(appendOpenOrderItemToProducts(product));
    }
    store.dispatch(removeOrderItemById(orderItemId));
    store.dispatch(removeOpenOrderItemById(orderItemId));
    if (reload) getOrdersById(orderId);
};

// Takes the item out of the lists now; the returned function puts it back where it was.
const takeOutOrderItem = (orderItemId) => {
    const { orderSlice, productsSlice } = store.getState();
    const saved = [
        { item: orderSlice?.orderItems?.find((item) => item.id === orderItemId), index: orderSlice?.orderItems?.findIndex((item) => item.id === orderItemId), reinsert: reinsertOrderItem },
        { item: productsSlice?.openOrderItems?.find((item) => item.id === orderItemId), index: productsSlice?.openOrderItems?.findIndex((item) => item.id === orderItemId), reinsert: reinsertOpenOrderItem },
    ];
    store.dispatch(removeOrderItemById(orderItemId));
    store.dispatch(removeOpenOrderItemById(orderItemId));

    return () => saved.forEach(({ item, index, reinsert }) => item && store.dispatch(reinsert({ item, index })));
};

export const removeOrderItem = async (orderItemId, orderId, product = null, { toast = true, isGet = true, optimistic = false, signal, ...options } = {}) => {
    if (isPendingId(orderItemId)) {
        return { ack: false, message: PENDING_ITEMS_MESSAGE };
    }

    const putBack = optimistic ? takeOutOrderItem(orderItemId) : null;
    // An edit of this item still on its way must reach the server before the item is deleted.
    await waitForItemEdits(orderItemId);
    let res;
    try {
        res = await handleDeleteRequest(
            URI.order_item,
            { id: orderItemId },
            {
                module: moduleName.ORDER_ITEM,
                loading: false,
                toast,
                signal,
                ...options,
            }
        );
    } catch (err) {
        putBack?.();
        throw err;
    }
    if (res?.ack) forgetSuggestedProducts();
    else putBack?.();
    if (res?.ack && isGet) {
        finishRemoveOrderItem(orderItemId, orderId, product);
    }
    return res;
};

// Where a submitted order is: processing, posted, failed or draft. A small answer, meant to be asked repeatedly.
export const getOrderStatus = async (orderId, signal) => {
    const res = await handleGetRequest(URI.order_status, null, {
        params: { id: orderId },
        loading: false,
        toast: false,
        signal,
    });
    return res;
};

export const postOrderToNetSuite = async (body, { toast = false } = {}) => {
    const res = await handlePostRequest(URI.post_orders, body, {
        module: moduleName.POST_ORDER,
        toast,
    });
    return res;
};

export const getInvoices = async (body, config = {}) => {
    const res = await handlePostRequest(URI.invoices, body, {
        module: moduleName.GET_INVOICES,
        ...config,
    });
    return res;
};

// With `withOrder` the answer also carries the order and its items, so the page that opens it needs no second request.
export const getOpenOrderByCustomer = async (customerNetsuiteId, { withOrder = false } = {}) => {
    const res = await handleGetRequest(URI.open_order, null, {
        toast: false,
        params: {
            customer_netsuite_id: customerNetsuiteId,
            ...(withOrder ? { with_order: 1 } : {}),
        },
    });
    return res;
};

export const updateTierPrice = async (body) => {
    const res = await handlePutRequest(URI.item_price, body, {
        loading: false,
        toast: false,
    });
    return res;
}

export const getOrderLink = async (orderId) => {
    const res = await handleGetRequest(URI.ns_link, null, {
        toast: false,
        params: {
            internal_id: orderId,
        },
    });
    return res;
}

export const addInvoiceNote = async (body) => {
    const res = await handlePostRequest(URI.invoice_note, body);
    return res;
}

export const updateNetSuiteOrder = async (body) => {
    const res = await handlePutRequest(URI.existing_ns_order, body, {});
    return res;
}

export const getExistingOrders = async (params) => {
    const res = await handleGetRequest(URI.existing_orders, null, {
        toast: false,
        params
    })

    return res
}

export const getIntentUrl = async (body) => {
    const res = await handleGetRequest(URI.intent_url, null, {
        toast: false,
        params: body,
    });
    if (res?.success) {
        return res?.url;
    }
    return null;
}

export const createPaymentIntent = async (body) => {
    const res = await handlePostRequest(URI.create_payment, body, {
        toast: false,
        loading: false,
    });
    return res;
};

export const checkPaymentStatus = async (body) => {
    const res = await handlePostRequest(URI.check_payment_status, body, {
        toast: false,
        loading: false,
    });
    return res;
};

export const clearOrderItems = async (body) => {
    const res = await handleDeleteRequest(URI.all_order_item, body, {
        loading: false,
        toast: false,
    });
    return res;
}
// Quantity, unit and tier edits of a saved item: shown at once, saved in the background, put back if refused.
export const editOrderItem = ({ id, changes, orderId, onRefused }) =>
    editSavedItem({
        id,
        changes,
        onRefused,
        save: (row) =>
            addOrderItem(
                {
                    id: row.id,
                    order_id: orderId,
                    product_netsuite_id: row.product_netsuite_id,
                    quantity: row.quantity,
                    uom: row.uom,
                    price_level: row.price_level,
                    unit_price: row.unit_price,
                },
                null,
                false
            ),
    });

// True when the item is part of the order on the page, so edits to it can be shown at once.
export const isOrderItemLoaded = (id) => !!id && store.getState().orderSlice.orderItems.some((item) => item.id === id);

// Edits of an item that is already on the order page, ignoring values the caller did not give.
export const updateLoadedOrderItem = ({ id, orderId, changes, onRefused }) =>
    editOrderItem({
        id,
        orderId,
        changes: Object.fromEntries(Object.entries(changes).filter(([, value]) => value !== undefined)),
        onRefused,
    });
